import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HelpCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';

import { ROUTES } from '#/config/routes';
import {
  createWorkOrderStockTransfer,
  fetchWorkOrderStockTransferCandidates,
  updateWorkOrderStockTransfer,
} from '#/api/momMgmt/workOrder/transfer';
import { Button } from '#/modules/commonComponents/button';
import { ScanCodeDialog, ScanTriggerButton } from '#/modules/commonComponents/scan';
import ColumnTipHeader from '#/modules/commonComponents/table/ColumnTipHeader';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import {
  getStockNoItemId,
  lookupStockNo,
  STOCK_NO_SCAN_LABELS,
} from '#/modules/imsMgmt/receipts/shared/scan';
import {
  extractUnitOptions,
  maxQtyInUnit,
} from '#/modules/imsMgmt/helper/materialUnit';
import InvStockPickerDialog from '#/modules/imsMgmt/receipts/stockTransfer/components/InvStockPickerDialog';
import { isFullTransferQty } from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';
import {
  getWorkOrderApiErrorMessage,
  isWorkOrderVersionConflict,
} from '#/modules/momMgmt/workOrder/helper/apiError';
import { warehouseLabelById } from '#/modules/momMgmt/workOrder/helper/warehouseLabelById';
import { mapTransferDraftToCreatePayload } from '#/modules/momMgmt/workOrder/transfer/helper/mapDraftToCreatePayload';
import {
  createClientLineId,
  ensureClientLineIds,
  reconcileDraftLinesWithCandidates,
} from '#/modules/momMgmt/workOrder/transfer/helper/formCachePayload';
import { FORM_CACHE_UI_STATUS } from '#/modules/momMgmt/workOrder/transfer/helper/formCacheUiStatus';
import { mapWorkOrderTransferDetailToDraft } from '#/modules/momMgmt/workOrder/transfer/helper/mapWorkOrderTransferDetailToDraft';
import { normalizeTransferCandidates } from '#/modules/momMgmt/workOrder/transfer/helper/normalizeTransferCandidates';
import { OTHER_MATERIAL_GROUP_KEY } from '#/modules/momMgmt/workOrder/transfer/helper/otherMaterialGroup';
import {
  applyWorkOrderDemand,
  resolveExcludeWarehouseIds,
  TRANSFER_CONFIG_MODE,
} from '#/modules/momMgmt/workOrder/transfer/helper/transferConfigMode';
import {
  renderWorkOrderTransferAmbiguousRow,
  renderWorkOrderTransferScannedRow,
} from '#/modules/momMgmt/workOrder/transfer/helper/workOrderTransferScanRender';
import { useWorkOrderTransferFormCache } from '#/modules/momMgmt/workOrder/transfer/hooks/useWorkOrderTransferFormCache';
import { dateToApiDate } from '#/utils/date';
import { clampQtyToAvailable } from '#/utils/quantityBounds';
import { toast } from '#/utils/toast';
import {
  workOrderStockTransferCandidatesQueryKey,
  workOrderStockTransferDetailQueryKey,
  workOrderStockTransferListQueryKeyPrefix,
} from '#/utils/queryKeys';

import WorkOrderTransferDraftLines from './WorkOrderTransferDraftLines';
import WorkOrderTransferHeader from './WorkOrderTransferHeader';
import WorkOrderTransferMaterialList from './WorkOrderTransferMaterialList';
import WorkOrderTransferPickerDialog from './WorkOrderTransferPickerDialog';
import WorkOrderTransferWorkOrderPickerDialog from './WorkOrderTransferWorkOrderPickerDialog';

const SAME_WAREHOUSE_SCAN_MESSAGE = '來源倉不得與排除倉別相同';
const TARGET_WAREHOUSE_SCAN_TIP = '請先選擇目標倉以載入需求彙總。';
const WORK_ORDER_SCAN_TIP = '請先選擇工單以載入該工單需求。';
const INV_STOCK_PICKER_TITLE = '選擇轉倉庫存（可能含非需求物料）';
const INV_STOCK_PICKER_DESCRIPTION = [
  '此處不限左欄需求清單。來源倉不得與目標倉（及指定工單工作倉）相同；',
  '若選到目標倉目前非需求的物料，確認後會列入右側「其他物料」。',
  '請優先從左側需求物料選庫。',
].join('');
const SCAN_WARNING_TITLE = '以下明細為非需求物料：';
const INV_STOCK_PICKER_TIP = '請優先選擇下方需求物料的庫存，如有特殊操作再透過此處做選擇物料轉倉';

function compareMtrSortKey(a, b) {
  const keyA = String(a?.mtrNo || a?.mtrId || '');
  const keyB = String(b?.mtrNo || b?.mtrId || '');
  return keyA.localeCompare(keyB, undefined, { numeric: true, sensitivity: 'base' });
}

function isExcludedSourceWarehouse(row, excludeWarehouseIds = []) {
  const warehouseId = row?.warehouse?.id;
  if (warehouseId == null || warehouseId === '') {
    return false;
  }
  const id = String(warehouseId);
  return (excludeWarehouseIds ?? []).some((excludeId) => String(excludeId) === id);
}

function matchesDemandMaterialKeyword(material, keyword) {
  const needle = String(keyword ?? '').trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const mtrNo = String(material?.mtrNo ?? '').toLowerCase();
  const mtrName = String(material?.mtrName ?? '').toLowerCase();
  return mtrNo.includes(needle) || mtrName.includes(needle);
}

function formatScanWarehouseLabel(row) {
  const warehouse = row?.warehouse;
  if (!warehouse) {
    return '';
  }
  const warehouseLabel = [warehouse.code, warehouse.name].filter(Boolean).join(' - ')
    || warehouse.name
    || '';
  const location = warehouse.location;
  const locationLabel = location
    ? ([location.code, location.name].filter(Boolean).join(' ') || '')
    : '';
  return [warehouseLabel, locationLabel].filter(Boolean).join(' / ');
}

/**
 * 掃描／選擇物料庫存列 → 工單轉倉 draft line（欄位對齊選庫 picker）。
 */
function mapScanRowToDraftLine(row, material, { isOtherMaterial = false } = {}) {
  const invStockId = row?.id;
  const mtrId = material?.mtrId || row?.mtr?.id;
  if (!invStockId || !mtrId) {
    return null;
  }

  const candidateStock = (material.transferableInventoryStocks ?? [])
    .find((stock) => stock.id === invStockId);

  let availableQty;
  let stockNo;
  let fromWarehouseLabel;

  if (candidateStock) {
    availableQty = candidateStock.availableQty;
    stockNo = candidateStock.stockNo;
    fromWarehouseLabel = [candidateStock.warehouseLabel, candidateStock.locationLabel]
      .filter(Boolean)
      .join(' / ');
  } else {
    availableQty = Number(row.availableQty ?? row.baseQty);
    stockNo = row.stockNo ?? '';
    fromWarehouseLabel = formatScanWarehouseLabel(row);
  }

  const unitOptions = extractUnitOptions(row?.mtr ?? material ?? row);
  const qty = clampQtyToAvailable(availableQty, availableQty);
  if (!(qty > 0)) {
    return null;
  }

  return {
    mtrId,
    mtrNo: material?.mtrNo ?? row?.mtr?.mtrNo ?? '',
    mtrName: material?.mtrName ?? row?.mtr?.name ?? '',
    invStockId,
    stockNo,
    qty,
    availableQty,
    unitId: '',
    unitOptions,
    fromWarehouseLabel,
    stockNoPolicy: 'TRANSFERRED_KEEP_OLD',
    isOtherMaterial,
    clientLineId: createClientLineId(),
    toLocationId: null,
    remark: null,
  };
}

const WorkOrderTransferShell = ({
  warehouseOptions = [],
  isWarehouseLoading = false,
  transferId = null,
  detail = null,
  onDetailRefetch = null,
  onFormCacheStatusChange = null,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(transferId);
  const initialMapped = detail && isEdit
    ? mapWorkOrderTransferDetailToDraft(detail)
    : null;
  const createPrefillWarehouseId = !isEdit
    ? (searchParams.get('warehouse_id') || '')
    : '';

  const [transferDate, setTransferDate] = useState(
    () => initialMapped?.transferDate || dateToApiDate(new Date()) || '',
  );
  const [targetWarehouseId, setTargetWarehouseId] = useState(
    () => initialMapped?.toWarehouseId || createPrefillWarehouseId || '',
  );
  const [draftLines, setDraftLines] = useState(
    () => ensureClientLineIds(initialMapped?.draftLines ?? []),
  );
  const [issueReason, setIssueReason] = useState(() => initialMapped?.issueReason ?? null);
  const [remark, setRemark] = useState(() => initialMapped?.remark ?? null);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [pickerMaterial, setPickerMaterial] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [workOrderPickerOpen, setWorkOrderPickerOpen] = useState(false);
  const [configMode, setConfigMode] = useState(
    TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE,
  );
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [stockNoScanOpen, setStockNoScanOpen] = useState(false);
  const [stockNoScanKey, setStockNoScanKey] = useState(0);
  const [invStockPickerOpen, setInvStockPickerOpen] = useState(false);
  const [invStockPickerKey, setInvStockPickerKey] = useState(0);
  const [focusedMtrId, setFocusedMtrId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  /** 右欄群組收合；僅記錄使用者摺疊，未列者預設展開。 */
  const [collapsedByMtrId, setCollapsedByMtrId] = useState({});
  const hydratedDetailIdRef = useRef(initialMapped ? (detail.id ?? transferId) : null);
  const updatedAtRef = useRef(initialMapped?.updatedAt ?? null);
  const skipCacheNotifyRef = useRef(false);
  const createSnapshotIdRef = useRef(null);

  const getFormSnapshot = useCallback(() => ({
    transferDate,
    targetWarehouseId,
    issueReason,
    remark,
    configMode,
    selectedWorkOrder,
    draftLines,
  }), [
    transferDate,
    targetWarehouseId,
    issueReason,
    remark,
    configMode,
    selectedWorkOrder,
    draftLines,
  ]);

  const applyRestoredForm = useCallback((restored) => {
    skipCacheNotifyRef.current = true;
    setTransferDate(restored.transferDate || '');
    setTargetWarehouseId(restored.targetWarehouseId || '');
    setIssueReason(restored.issueReason ?? '');
    setRemark(restored.remark ?? '');
    setConfigMode(
      restored.configMode || TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE,
    );
    setSelectedWorkOrder(restored.selectedWorkOrder ?? null);
    setDraftLines(ensureClientLineIds(restored.draftLines ?? []));
    setKeywordDraft('');
    setFocusedMtrId(null);
    setCollapsedByMtrId({});
  }, []);

  const formCache = useWorkOrderTransferFormCache({
    enabled: !isEdit,
    getFormSnapshot,
    applyRestoredForm,
  });

  const {
    enabled: formCacheEnabled,
    isHydrating: formCacheHydrating,
    uiStatus: formCacheUiStatus,
    uiStatusLabel: formCacheUiStatusLabel,
    autosaveStopped: formCacheAutosaveStopped,
    confirmDialog: formCacheConfirmDialog,
    notifyFormChanged,
    flushForSubmit,
    onCreateSuccess: onFormCacheCreateSuccess,
    handleStartNew,
    handleResolveConflict,
  } = formCache;

  useEffect(() => {
    if (isEdit || !onFormCacheStatusChange) {
      return;
    }
    if (!formCacheUiStatusLabel) {
      onFormCacheStatusChange(null);
      return;
    }
    const actions = [];
    if (formCacheUiStatus === FORM_CACHE_UI_STATUS.CONFLICT) {
      actions.push({
        id: 'resolve',
        label: '選擇版本',
        onClick: handleResolveConflict,
      });
    } else if (
      formCacheUiStatus === FORM_CACHE_UI_STATUS.CONSUMED
      || formCacheAutosaveStopped
    ) {
      actions.push({
        id: 'startNew',
        label: '建立新草稿',
        onClick: handleStartNew,
      });
    }
    onFormCacheStatusChange({
      status: formCacheUiStatus,
      label: formCacheUiStatusLabel,
      actions,
    });
  }, [
    isEdit,
    formCacheAutosaveStopped,
    formCacheUiStatus,
    formCacheUiStatusLabel,
    handleResolveConflict,
    handleStartNew,
    onFormCacheStatusChange,
  ]);

  useEffect(() => {
    if (isEdit || !onFormCacheStatusChange) {
      return undefined;
    }
    return () => {
      onFormCacheStatusChange(null);
    };
  }, [isEdit, onFormCacheStatusChange]);

  useEffect(() => {
    if (isEdit) {
      return;
    }
    if (!searchParams.get('warehouse_id')) {
      return;
    }
    if (formCacheHydrating) {
      return;
    }
    setSearchParams({}, { replace: true });
  }, [isEdit, searchParams, setSearchParams, formCacheHydrating]);

  useEffect(() => {
    if (!formCacheEnabled || formCacheHydrating) {
      return;
    }
    // resume／回填後至少再 skip 一輪，避免 isHydrating→false 吃掉 skip 後立刻 PUT
    if (skipCacheNotifyRef.current) {
      skipCacheNotifyRef.current = false;
      return;
    }
    notifyFormChanged();
  }, [
    transferDate,
    targetWarehouseId,
    issueReason,
    remark,
    configMode,
    selectedWorkOrder,
    draftLines,
    formCacheEnabled,
    formCacheHydrating,
    notifyFormChanged,
  ]);

  useEffect(() => {
    if (!isEdit || !detail) {
      return;
    }
    const detailId = detail.id ?? transferId;
    if (hydratedDetailIdRef.current === detailId) {
      updatedAtRef.current = detail.updatedAt
        ?? detail.record?.updatedAt
        ?? updatedAtRef.current;
      return;
    }

    const mapped = mapWorkOrderTransferDetailToDraft(detail);
    hydratedDetailIdRef.current = detailId;
    updatedAtRef.current = mapped.updatedAt;
    setTransferDate(mapped.transferDate || '');
    setTargetWarehouseId(mapped.toWarehouseId || '');
    setDraftLines(ensureClientLineIds(mapped.draftLines));
    setIssueReason(mapped.issueReason);
    setRemark(mapped.remark);
    setKeywordDraft('');
    setFocusedMtrId(null);
    setCollapsedByMtrId({});
  }, [detail, isEdit, transferId]);

  const canScanStockNo = Boolean(targetWarehouseId);

  const candidatesQuery = useQuery({
    queryKey: workOrderStockTransferCandidatesQueryKey({
      workWarehouseId: targetWarehouseId,
    }),
    queryFn: () => fetchWorkOrderStockTransferCandidates({
      workWarehouseId: targetWarehouseId,
    }),
    enabled: Boolean(targetWarehouseId),
  });

  const candidatesNormalized = useMemo(
    () => normalizeTransferCandidates(candidatesQuery.data?.data),
    [candidatesQuery.data],
  );

  const materials = candidatesNormalized.materials;

  const displayMaterials = useMemo(() => {
    if (configMode !== TRANSFER_CONFIG_MODE.BY_WORK_ORDER) {
      return materials;
    }
    if (!selectedWorkOrder?.id) {
      return [];
    }
    return applyWorkOrderDemand(materials, selectedWorkOrder.id);
  }, [configMode, materials, selectedWorkOrder]);

  const excludeWarehouseIds = useMemo(
    () => resolveExcludeWarehouseIds({
      targetWarehouseId,
      selectedWorkOrder: configMode === TRANSFER_CONFIG_MODE.BY_WORK_ORDER
        ? selectedWorkOrder
        : null,
    }),
    [configMode, selectedWorkOrder, targetWarehouseId],
  );

  useEffect(() => {
    if (!targetWarehouseId || !candidatesQuery.isSuccess) {
      return;
    }
    if (draftLines.length === 0) {
      return;
    }
    const { lines, removedCount, changed } = reconcileDraftLinesWithCandidates(
      draftLines,
      displayMaterials.length > 0 ? displayMaterials : materials,
    );
    if (!changed) {
      return;
    }
    // candidates 對齊必須寫回 draft state（勿只改 UI 衍生），並 toast 移除數
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconcile write-back
    setDraftLines(ensureClientLineIds(lines));
    if (removedCount > 0) {
      toast.info(`已移除 ${removedCount} 筆目前不可用的庫存`);
    }
  }, [
    targetWarehouseId,
    candidatesQuery.isSuccess,
    displayMaterials,
    materials,
    draftLines,
  ]);

  const demandMtrIds = useMemo(
    () => new Set(
      displayMaterials.map((material) => material.mtrId).filter(Boolean),
    ),
    [displayMaterials],
  );

  const materialsSorted = useMemo(
    () => [...displayMaterials].sort(compareMtrSortKey),
    [displayMaterials],
  );

  const filteredMaterials = useMemo(
    () => materialsSorted.filter((material) => (
      matchesDemandMaterialKeyword(material, keywordDraft)
    )),
    [materialsSorted, keywordDraft],
  );

  const sortedDraftLines = useMemo(
    () => [...draftLines].sort(compareMtrSortKey),
    [draftLines],
  );

  const operatingByMtrId = useMemo(() => {
    const map = {};
    draftLines.forEach((line) => {
      if (!line?.mtrId || line.isOtherMaterial) {
        return;
      }
      map[line.mtrId] = (map[line.mtrId] || 0) + (Number(line.qty) || 0);
    });
    return map;
  }, [draftLines]);

  const draftLinesByMtrId = useMemo(() => {
    const map = {};
    draftLines.forEach((line) => {
      if (!line?.mtrId || line.isOtherMaterial) {
        return;
      }
      if (!map[line.mtrId]) {
        map[line.mtrId] = [];
      }
      map[line.mtrId].push({
        stockNo: line.stockNo,
        qty: line.qty,
      });
    });
    return map;
  }, [draftLines]);

  const pickerInitialSelection = useMemo(() => {
    if (!pickerMaterial?.mtrId) {
      return {};
    }
    const map = {};
    draftLines.forEach((line) => {
      if (line.mtrId === pickerMaterial.mtrId && line.invStockId) {
        map[line.invStockId] = Number(line.qty) || 0;
      }
    });
    return map;
  }, [draftLines, pickerMaterial]);

  const saveMutation = useMutation({
    mutationFn: (payload) => {
      if (isEdit) {
        return updateWorkOrderStockTransfer(transferId, payload);
      }
      return createWorkOrderStockTransfer(payload, {
        formCacheSnapshotId: createSnapshotIdRef.current,
      });
    },
    onSuccess: async (result) => {
      toast.success(result?.message || (
        isEdit ? '更新轉倉單成功' : '建立轉倉單成功'
      ));
      if (!isEdit) {
        await onFormCacheCreateSuccess();
      }
      await queryClient.invalidateQueries({
        queryKey: workOrderStockTransferListQueryKeyPrefix(),
      });
      if (isEdit && transferId) {
        const nextUpdatedAt = result?.data?.updatedAt ?? result?.data?.record?.updatedAt;
        if (nextUpdatedAt) {
          updatedAtRef.current = nextUpdatedAt;
        }
        await queryClient.invalidateQueries({
          queryKey: workOrderStockTransferDetailQueryKey(transferId),
        });
      }
      if (targetWarehouseId) {
        await queryClient.invalidateQueries({
          queryKey: workOrderStockTransferCandidatesQueryKey({
            workWarehouseId: targetWarehouseId,
          }),
        });
      }
      navigate(isEdit
        ? ROUTES.workOrderTransferDetail(transferId)
        : ROUTES.workOrderTransfer);
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(
        error,
        isEdit ? '更新轉倉單失敗' : '建立轉倉單失敗',
      ));
      if (isEdit && isWorkOrderVersionConflict(error)) {
        hydratedDetailIdRef.current = null;
        onDetailRefetch?.();
      }
    },
  });

  function clearDraftSelectionState() {
    setKeywordDraft('');
    setDraftLines([]);
    setFocusedMtrId(null);
    setCollapsedByMtrId({});
    setInvStockPickerOpen(false);
    setPickerOpen(false);
    setPickerMaterial(null);
  }

  function handleTargetWarehouseChange(nextId) {
    setTargetWarehouseId(nextId ?? '');
    clearDraftSelectionState();
  }

  function handleConfigModeChange(nextMode) {
    const mode = nextMode || TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE;
    setConfigMode(mode);
    if (mode === TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE) {
      setSelectedWorkOrder(null);
      clearDraftSelectionState();
      return;
    }
    // 指定工單：目標倉改由工單工作倉帶入
    setSelectedWorkOrder(null);
    setTargetWarehouseId('');
    clearDraftSelectionState();
    setWorkOrderPickerOpen(true);
  }

  function handleWorkOrderSelect(workOrder) {
    const next = workOrder?.id
      ? {
        id: String(workOrder.id),
        workOrderNo: workOrder.workOrderNo == null
          ? ''
          : String(workOrder.workOrderNo),
        workWarehouseId: workOrder.workWarehouseId == null
          || workOrder.workWarehouseId === ''
          ? null
          : String(workOrder.workWarehouseId),
      }
      : null;
    setSelectedWorkOrder(next);
    if (next?.workWarehouseId) {
      setTargetWarehouseId(next.workWarehouseId);
    }
    clearDraftSelectionState();
  }

  function handleClearWorkOrder() {
    setSelectedWorkOrder(null);
    setTargetWarehouseId('');
    clearDraftSelectionState();
  }

  function handleOpenPicker(material) {
    setPickerMaterial(material);
    setPickerOpen(true);
  }

  const missingSourceTip = configMode === TRANSFER_CONFIG_MODE.BY_WORK_ORDER
    ? WORK_ORDER_SCAN_TIP
    : TARGET_WAREHOUSE_SCAN_TIP;

  function handleOpenInvStockPicker() {
    if (!targetWarehouseId) {
      toast.info(missingSourceTip);
      return;
    }
    setInvStockPickerKey((prev) => prev + 1);
    setInvStockPickerOpen(true);
  }

  function handleOpenStockNoScan() {
    if (!canScanStockNo) {
      toast.info(missingSourceTip);
      return;
    }
    setStockNoScanKey((prev) => prev + 1);
    setStockNoScanOpen(true);
  }

  async function lookupStockNoForTransfer(code) {
    const result = await lookupStockNo(code);
    const raw = result.items || [];
    const otherWarehouseItems = raw.filter(
      (row) => !isExcludedSourceWarehouse(row, excludeWarehouseIds),
    );
    if (raw.length > 0 && otherWarehouseItems.length === 0) {
      throw Object.assign(new Error('same-warehouse'), { code: 'SAME_WAREHOUSE' });
    }
    return { items: otherWarehouseItems };
  }

  function appendInvStockRows(selectedRows, { notifyDuplicate = false } = {}) {
    const materialsById = new Map(
      materials.map((material) => [material.mtrId, material]),
    );
    const existingIds = new Set(
      draftLines.map((line) => line.invStockId).filter(Boolean),
    );
    const appended = [];

    (selectedRows || []).forEach((row) => {
      const id = row?.id;
      if (!id) {
        return;
      }
      if (isExcludedSourceWarehouse(row, excludeWarehouseIds)) {
        toast.error(SAME_WAREHOUSE_SCAN_MESSAGE);
        return;
      }
      if (existingIds.has(id)) {
        if (notifyDuplicate) {
          toast.error('此庫存已在明細中，已跳過');
        }
        return;
      }
      const mtrId = row?.mtr?.id;
      const material = mtrId ? materialsById.get(mtrId) : null;
      const isOtherMaterial = !material;
      const line = mapScanRowToDraftLine(
        row,
        material || {
          mtrId,
          mtrNo: row?.mtr?.mtrNo ?? '',
          mtrName: row?.mtr?.name ?? '',
        },
        { isOtherMaterial },
      );
      if (!line) {
        return;
      }
      appended.push(line);
      existingIds.add(id);
    });

    if (appended.length === 0) {
      return;
    }

    setDraftLines((prev) => ensureClientLineIds([...prev, ...appended]));
    const last = appended[appended.length - 1];
    handleFocusMaterial(
      last.isOtherMaterial ? OTHER_MATERIAL_GROUP_KEY : last.mtrId,
    );
  }

  function handleStockNoScanConfirm(selectedRows) {
    appendInvStockRows(selectedRows, { notifyDuplicate: true });
  }

  function handleInvStockPickerConfirm(selectedRows) {
    appendInvStockRows(selectedRows);
  }

  function handleFocusMaterial(mtrId) {
    if (!mtrId) {
      return;
    }
    setFocusedMtrId(mtrId);
    setFocusToken((prev) => prev + 1);
    setCollapsedByMtrId((prev) => ({ ...prev, [mtrId]: false }));
  }

  function handleToggleDraftGroup(mtrId) {
    setCollapsedByMtrId((prev) => ({
      ...prev,
      [mtrId]: prev[mtrId] !== true,
    }));
  }

  function handlePickerConfirm(lines) {
    const mtrId = pickerMaterial?.mtrId;
    if (!mtrId) {
      return;
    }
    const nextLines = ensureClientLineIds(Array.isArray(lines) ? lines : []);
    setDraftLines((prev) => [
      ...prev.filter((item) => item.mtrId !== mtrId || item.isOtherMaterial),
      ...nextLines,
    ]);
    if (nextLines.length > 0) {
      handleFocusMaterial(mtrId);
    } else if (focusedMtrId === mtrId) {
      setFocusedMtrId(null);
    }
  }

  function handleRemoveDraftLine(sortedIndex) {
    const lineToRemove = sortedDraftLines[sortedIndex];
    if (!lineToRemove?.invStockId) {
      return;
    }
    setDraftLines((prev) => prev.filter((line) => line.invStockId !== lineToRemove.invStockId));
  }

  function handleDraftQtyChange(sortedIndex, nextQty, { finalize = false, maxQty } = {}) {
    const target = sortedDraftLines[sortedIndex];
    if (!target?.invStockId) {
      return;
    }
    const availableBase = Number(target.availableQty) || 0;
    const unitMax = maxQty != null
      ? Number(maxQty)
      : maxQtyInUnit(availableBase, target.unitOptions, target.unitId);
    const clampMax = Math.max(unitMax > 0 ? unitMax : availableBase, 1);
    let qty;
    if (!finalize && (nextQty === '' || nextQty == null)) {
      qty = '';
    } else if (!finalize) {
      const parsed = Number(nextQty);
      qty = Number.isFinite(parsed) ? parsed : target.qty;
    } else {
      let next = clampQtyToAvailable(nextQty, clampMax);
      next = Math.floor(Number(next)) || 1;
      if (unitMax > 0) {
        next = Math.min(next, unitMax);
      }
      qty = next;
    }
    const fullTransfer = isFullTransferQty(
      qty,
      availableBase,
      target.unitOptions,
      target.unitId,
    );
    setDraftLines((prev) => prev.map((line) => {
      if (line.invStockId !== target.invStockId) {
        return line;
      }
      return {
        ...line,
        qty,
        stockNoPolicy: fullTransfer
          ? 'TRANSFERRED_KEEP_OLD'
          : (line.stockNoPolicy || 'TRANSFERRED_KEEP_OLD'),
      };
    }));
  }

  function handleDraftUnitChange(sortedIndex, nextUnitId) {
    const target = sortedDraftLines[sortedIndex];
    if (!target?.invStockId) {
      return;
    }
    const availableBase = Number(target.availableQty) || 0;
    const max = maxQtyInUnit(availableBase, target.unitOptions, nextUnitId);
    const clamped = clampQtyToAvailable(target.qty, Math.max(max, 1));
    const nextQty = max > 0 ? Math.min(clamped, max) : clamped;
    const fullTransfer = isFullTransferQty(
      nextQty,
      availableBase,
      target.unitOptions,
      nextUnitId,
    );
    setDraftLines((prev) => prev.map((line) => {
      if (line.invStockId !== target.invStockId) {
        return line;
      }
      return {
        ...line,
        unitId: nextUnitId,
        qty: nextQty,
        stockNoPolicy: fullTransfer
          ? 'TRANSFERRED_KEEP_OLD'
          : (line.stockNoPolicy || 'TRANSFERRED_KEEP_OLD'),
      };
    }));
  }

  function handleDraftUnitOptionsChange(sortedIndex, unitOptions) {
    const target = sortedDraftLines[sortedIndex];
    if (!target?.invStockId) {
      return;
    }
    setDraftLines((prev) => prev.map((line) => (
      line.invStockId === target.invStockId
        ? { ...line, unitOptions }
        : line
    )));
  }

  function handleDraftStockNoPolicyChange(sortedIndex, nextPolicy) {
    const target = sortedDraftLines[sortedIndex];
    if (!target?.invStockId) {
      return;
    }
    const availableBase = Number(target.availableQty) || 0;
    if (isFullTransferQty(
      target.qty,
      availableBase,
      target.unitOptions,
      target.unitId,
    )) {
      return;
    }
    setDraftLines((prev) => prev.map((line) => (
      line.invStockId === target.invStockId
        ? { ...line, stockNoPolicy: nextPolicy || 'TRANSFERRED_KEEP_OLD' }
        : line
    )));
  }

  async function handleSubmit() {
    if (saveMutation.isPending) {
      return;
    }
    if (!transferDate) {
      toast.error('請填寫轉倉日');
      return;
    }
    if (!targetWarehouseId) {
      toast.error('請選擇目標倉');
      return;
    }
    if (!draftLines.length) {
      toast.error('請至少加入一筆明細');
      return;
    }
    const normalizedLines = draftLines.map((line) => {
      const availableQty = Number(line.availableQty) || 0;
      const qty = clampQtyToAvailable(
        line.qty,
        maxQtyInUnit(availableQty, line.unitOptions, line.unitId) || availableQty,
      );
      const fullTransfer = isFullTransferQty(
        qty,
        availableQty,
        line.unitOptions,
        line.unitId,
      );
      return {
        ...line,
        qty,
        stockNoPolicy: fullTransfer
          ? 'TRANSFERRED_KEEP_OLD'
          : (line.stockNoPolicy || 'TRANSFERRED_KEEP_OLD'),
      };
    });
    const payload = mapTransferDraftToCreatePayload({
      transferDate,
      toWarehouseId: targetWarehouseId,
      issueReason,
      remark,
      draftLines: normalizedLines,
    });
    if (isEdit) {
      payload.updatedAt = updatedAtRef.current;
      saveMutation.mutate(payload);
      return;
    }

    if (formCacheEnabled) {
      const flush = await flushForSubmit();
      if (!flush.ok) {
        return;
      }
      createSnapshotIdRef.current = flush.formCacheSnapshotId;
    } else {
      createSnapshotIdRef.current = null;
    }
    saveMutation.mutate(payload);
  }

  const canSubmit = draftLines.length > 0
    && Boolean(targetWarehouseId)
    && Boolean(transferDate)
    && !saveMutation.isPending
    && !formCacheHydrating
    && !formCacheAutosaveStopped;

  const actionLabel = saveMutation.isPending
    ? (isEdit ? '儲存中…' : '送出中…')
    : (isEdit ? '儲存' : '送出');

  const isWorkOrderMode = configMode === TRANSFER_CONFIG_MODE.BY_WORK_ORDER;

  let emptyHint = '請先選擇目標倉以載入需求彙總。';
  if (targetWarehouseId && candidatesQuery.isLoading) {
    emptyHint = '載入需求彙總中…';
  } else if (targetWarehouseId && candidatesQuery.isError) {
    emptyHint = getWorkOrderApiErrorMessage(candidatesQuery.error, '載入需求彙總失敗');
  } else if (isWorkOrderMode && !selectedWorkOrder?.id) {
    emptyHint = WORK_ORDER_SCAN_TIP;
  } else if (targetWarehouseId && displayMaterials.length === 0) {
    emptyHint = isWorkOrderMode
      ? `工單『${selectedWorkOrder?.workOrderNo || selectedWorkOrder?.id}』目前無轉倉需求。`
      : `『${warehouseLabelById(warehouseOptions, targetWarehouseId)}』 目前無轉倉需求。`;
  } else if (targetWarehouseId && filteredMaterials.length === 0) {
    emptyHint = '找不到符合關鍵字。';
  }

  return (
    <>
      {formCacheConfirmDialog}
      <div className="space-y-4">
        <WorkOrderTransferHeader
          warehouseOptions={warehouseOptions}
          targetWarehouseId={targetWarehouseId}
          onTargetWarehouseChange={handleTargetWarehouseChange}
          transferDate={transferDate}
          onTransferDateChange={setTransferDate}
          issueReason={issueReason ?? ''}
          onIssueReasonChange={setIssueReason}
          remark={remark ?? ''}
          onRemarkChange={setRemark}
          isWarehouseLoading={isWarehouseLoading}
          configMode={configMode}
          onConfigModeChange={handleConfigModeChange}
          selectedWorkOrder={selectedWorkOrder}
          onOpenWorkOrderPicker={() => setWorkOrderPickerOpen(true)}
          onClearWorkOrder={handleClearWorkOrder}
        />

        <div
          className="
            grid grid-cols-1 gap-4
            @4xl/sheet:grid-cols-2
          "
        >
          <section className="form-section min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="form-subsection__title mb-0!">
                <ColumnTipHeader
                  label="需求物料"
                  tip="點選列可在右側查看已加入庫存"
                />
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!targetWarehouseId}
                      onClick={handleOpenInvStockPicker}
                      className="gap-1"
                    >
                      選擇物料
                      <HelpCircle size={14} aria-hidden />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {INV_STOCK_PICKER_TIP}
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="mt-3 stock-ops-panel-scroll overflow-x-auto">
              <WorkOrderTransferMaterialList
                materials={filteredMaterials}
                operatingByMtrId={operatingByMtrId}
                draftLinesByMtrId={draftLinesByMtrId}
                focusedMtrId={focusedMtrId}
                focusToken={focusToken}
                onOpenPicker={handleOpenPicker}
                onFocusMaterial={handleFocusMaterial}
                emptyHint={emptyHint}
                keyword={keywordDraft}
                onKeywordChange={setKeywordDraft}
                keywordDisabled={!targetWarehouseId}
                showOnHandQty={!isWorkOrderMode}
              />
            </div>
          </section>

          <section className="form-section min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="form-subsection__title mb-0!">
                <ColumnTipHeader
                  label="已加入明細"
                  tip="點標題列會展開／收合；點子項目會對應左側的可用物料"
                />
              </h3>
              <ScanTriggerButton
                labels={STOCK_NO_SCAN_LABELS}
                disabled={!canScanStockNo}
                onClick={handleOpenStockNoScan}
              />
            </div>
            <div className="mt-3 stock-ops-panel-scroll">
              <WorkOrderTransferDraftLines
                lines={sortedDraftLines}
                onRemove={handleRemoveDraftLine}
                onFocusMaterial={handleFocusMaterial}
                onQtyChange={handleDraftQtyChange}
                onUnitChange={handleDraftUnitChange}
                onUnitOptionsChange={handleDraftUnitOptionsChange}
                onStockNoPolicyChange={handleDraftStockNoPolicyChange}
                collapsedByMtrId={collapsedByMtrId}
                onToggleGroup={handleToggleDraftGroup}
                focusedMtrId={focusedMtrId}
                focusToken={focusToken}
              />
            </div>
          </section>
        </div>
      </div>

      <div className="create_edit_form__actions stock-ops-actions mt-4 flex flex-wrap justify-end gap-2">
        <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
          {actionLabel}
        </Button>
      </div>

      <WorkOrderTransferPickerDialog
        key={pickerMaterial?.mtrId ?? 'closed'}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        material={pickerMaterial}
        initialSelection={pickerInitialSelection}
        onConfirm={handlePickerConfirm}
        excludeWarehouseIds={excludeWarehouseIds}
        showOnHandQty={!isWorkOrderMode}
      />

      <WorkOrderTransferWorkOrderPickerDialog
        open={workOrderPickerOpen}
        onOpenChange={setWorkOrderPickerOpen}
        selectedWorkOrderId={selectedWorkOrder?.id || null}
        onConfirm={handleWorkOrderSelect}
      />

      <ScanCodeDialog
        key={`stock-no-scan-${stockNoScanKey}`}
        open={stockNoScanOpen}
        onOpenChange={setStockNoScanOpen}
        labels={STOCK_NO_SCAN_LABELS}
        lookup={lookupStockNoForTransfer}
        getItemId={getStockNoItemId}
        renderScannedRow={renderWorkOrderTransferScannedRow}
        renderAmbiguousRow={renderWorkOrderTransferAmbiguousRow}
        onConfirm={handleStockNoScanConfirm}
        getErrorMessage={(error) => {
          if (error?.code === 'SAME_WAREHOUSE') {
            return SAME_WAREHOUSE_SCAN_MESSAGE;
          }
          return error?.message || '查詢庫存編號失敗';
        }}
        isWarningRow={(row) => {
          const mtrId = row?.mtr?.id;
          return !mtrId || !demandMtrIds.has(mtrId);
        }}
        warningTitle={SCAN_WARNING_TITLE}
      />

      <InvStockPickerDialog
        key={`inv-stock-picker-${invStockPickerKey}`}
        open={invStockPickerOpen}
        onOpenChange={setInvStockPickerOpen}
        excludeWarehouseId={targetWarehouseId || null}
        title={INV_STOCK_PICKER_TITLE}
        description={INV_STOCK_PICKER_DESCRIPTION}
        onConfirm={handleInvStockPickerConfirm}
      />
    </>
  );
};

WorkOrderTransferShell.propTypes = {
  warehouseOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  isWarehouseLoading: PropTypes.bool,
  transferId: PropTypes.string,
  detail: PropTypes.object,
  onDetailRefetch: PropTypes.func,
  onFormCacheStatusChange: PropTypes.func,
};

export default WorkOrderTransferShell;
